import { createElement, useCallback, useState } from 'react';
import axios from 'axios';
import { Flag, Reply, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { SentimentPill, VoteBar } from '@/components/CommunityPostBits';
import CommentGearMenu from '@/components/CommunityGearMenu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function timeAgo(iso) {
  if (!iso) return '';
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return new Date(iso).toLocaleDateString();
  } catch {
    return '';
  }
}

function CommentMeta({ comment, isDeleted, tp, collapsed, setCollapsed, actions }) {
  const editedTag = comment.edited_at ? <span className="italic">(edited)</span> : null;
  const sentimentTag = !isDeleted ? (
    <SentimentPill label={comment.sentiment_label} score={comment.sentiment_score} />
  ) : null;
  const deletedTag = isDeleted ? (
    <span className="px-1.5 py-0.5 bg-zinc-100 dark:bg-zinc-800 rounded text-[10px] font-bold">
      deleted by {comment.deleted_by}
    </span>
  ) : null;
  const reportTag = comment.is_admin_view && comment.report_count > 0 ? (
    <span className="ml-1 px-1.5 py-0.5 bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 rounded text-[10px] font-bold">
      ⚑ {comment.report_count}
    </span>
  ) : null;
  return (
    <div className="flex items-center gap-2 text-[11px] text-muted-foreground mb-1 flex-wrap">
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="hover:text-foreground p-0.5"
        aria-label={collapsed ? 'Expand' : 'Collapse'}
      >
        {collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      <span className="font-semibold text-foreground">@{comment.handle}</span>
      <span>·</span>
      <span>{timeAgo(comment.created_at)}</span>
      {editedTag}
      {sentimentTag}
      {reportTag}
      {deletedTag}
      <div className="ml-auto flex items-center gap-1">{actions}</div>
    </div>
  );
}

function CommentEditor({ value, setValue, onSave, onCancel, tp }) {
  return (
    <div className="space-y-2 mt-1">
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="w-full text-sm border border-border rounded p-2 bg-background"
        rows={3}
        maxLength={4000}
        data-testid={`${tp}-edit-textarea`}
      />
      <div className="flex gap-2">
        <Button size="sm" onClick={onSave} data-testid={`${tp}-edit-save`}>Save</Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

function ReplyForm({ value, setValue, onSubmit, onCancel, tp }) {
  return (
    <div className="space-y-2 mt-2">
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Write a reply…"
        className="w-full text-sm border border-border rounded p-2 bg-background"
        rows={2}
        maxLength={4000}
        data-testid={`${tp}-reply-textarea`}
      />
      <div className="flex gap-2">
        <Button size="sm" onClick={onSubmit} data-testid={`${tp}-reply-submit`}>Reply</Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

export default function CommentNode({ comment, allComments, postId, onMutate, depth = 0 }) {
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editBody, setEditBody] = useState(comment.body || '');
  const [collapsed, setCollapsed] = useState(false);
  const tp = `comment-${comment.comment_id}`;

  const children = allComments.filter((c) => c.parent_comment_id === comment.comment_id);
  const maxDepthReached = (comment.depth || depth) >= 5;
  const isDeleted = comment.deleted;

  const handleVote = useCallback(async (val) => {
    try {
      await axios.post(`${API}/community/comments/${comment.comment_id}/vote`, { value: val }, { withCredentials: true });
      onMutate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Vote failed.');
    }
  }, [comment.comment_id, onMutate]);

  const handleReplySubmit = async () => {
    const body = replyBody.trim();
    if (!body) return;
    try {
      await axios.post(`${API}/community/posts/${postId}/comments`, { body, parent_comment_id: comment.comment_id }, { withCredentials: true });
      setReplyBody('');
      setReplyOpen(false);
      onMutate();
      toast.success('Reply posted.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reply failed.');
    }
  };

  const handleEditSubmit = async () => {
    const body = editBody.trim();
    if (!body) return;
    try {
      await axios.patch(`${API}/community/comments/${comment.comment_id}`, { body }, { withCredentials: true });
      setEditOpen(false);
      onMutate();
      toast.success('Comment updated.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Edit failed.');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await axios.delete(`${API}/community/comments/${comment.comment_id}`, { withCredentials: true });
      onMutate();
      toast.success('Comment deleted.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed.');
    }
  };

  const handleReport = async () => {
    const reason = window.prompt('Report this comment — reason (optional):', '');
    if (reason === null) return;
    try {
      await axios.post(`${API}/community/comments/${comment.comment_id}/report`, { reason }, { withCredentials: true });
      toast.success('Thanks — moderators will review.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Report failed.');
    }
  };

  const indent = Math.min(comment.depth || depth, 4) * 14;

  const gear = (comment.can_edit || comment.can_delete) ? (
    <CommentGearMenu
      canEdit={comment.can_edit}
      canDelete={comment.can_delete}
      onEdit={() => { setEditBody(comment.body); setEditOpen(true); }}
      onDelete={handleDelete}
      onReport={!comment.can_edit ? handleReport : null}
      testidPrefix={tp}
    />
  ) : (
    <button
      type="button"
      onClick={handleReport}
      className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-amber-500"
      title="Report"
      data-testid={`${tp}-report-btn`}
    >
      <Flag className="w-3.5 h-3.5" />
    </button>
  );

  const actions = (
    <>
      <VoteBar score={comment.score} viewerVote={comment.viewer_vote} onVote={handleVote} testidPrefix={tp} />
      {gear}
    </>
  );

  const bodyEl = !collapsed && !editOpen ? (
    <p className={isDeleted ? 'text-sm whitespace-pre-wrap italic text-muted-foreground' : 'text-sm whitespace-pre-wrap'}>
      {comment.body}
    </p>
  ) : null;

  const editorEl = !collapsed && editOpen ? (
    <CommentEditor value={editBody} setValue={setEditBody} onSave={handleEditSubmit} onCancel={() => setEditOpen(false)} tp={tp} />
  ) : null;

  const replyBtnEl = !collapsed && !isDeleted && !maxDepthReached ? (
    <button
      type="button"
      onClick={() => setReplyOpen((o) => !o)}
      className="mt-1 text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1"
      data-testid={`${tp}-reply-btn`}
    >
      <Reply className="w-3 h-3" /> Reply
    </button>
  ) : null;

  const replyFormEl = !collapsed && replyOpen ? (
    <ReplyForm value={replyBody} setValue={setReplyBody} onSubmit={handleReplySubmit} onCancel={() => setReplyOpen(false)} tp={tp} />
  ) : null;

  const childrenEl = !collapsed && children.length > 0 ? (
    <div>
      {children.map((child) => createElement(CommentNode, {
        key: child.comment_id,
        comment: child,
        allComments,
        postId,
        onMutate,
        depth: depth + 1,
      }))}
    </div>
  ) : null;

  return (
    <div style={{ marginLeft: indent }} className="mt-2" data-testid={tp}>
      <div className="rounded-md border-l-2 border-violet-200 dark:border-violet-800 bg-muted/20 px-3 py-2">
        <CommentMeta
          comment={comment}
          isDeleted={isDeleted}
          tp={tp}
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          actions={actions}
        />
        {bodyEl}
        {editorEl}
        {replyBtnEl}
        {replyFormEl}
      </div>
      {childrenEl}
    </div>
  );
}
